import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, User, FileText, Camera, Send, MapPin,
  ShieldCheck, QrCode, Plus, Trash2, ChevronRight, CheckSquare
} from 'lucide-react';
import Swal from 'sweetalert2';
import { readHandovers, writeHandovers } from '../utils/handoverStorage';
import TomSelect from 'tom-select';
import QRCode from 'react-qr-code';
import HandoverDeliveryModal, { HANDOVER_MODES } from './HandoverDeliveryModal';

// ─── Step ────────────────────────────────────────────────────────────────────
const STEP = { FORM: 'form', SELECT: 'select', QR: 'qr' };

const newItem = () => ({
  _key: Math.random().toString(36).substr(2, 9),
  item_name: '',
  category: 'Barang',
  notes: '',
  photo: null,
  kantor: '',
  recipient_id: '',
  recipient_name: '',
  recipient_type: 'internal',
  tracking_token: '',
});

// ─── TomSelect wrapper (per-instance, per-item) ───────────────────────────
const TomSelectField = ({ selectRef, options, onChange, placeholder, disabled }) => {
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!selectRef.current) return;

    if (instanceRef.current) {
      instanceRef.current.clearOptions();
      if (options.length > 0) {
        instanceRef.current.addOptions(options);
        instanceRef.current.refreshOptions(false);
      }
      const cur = instanceRef.current.getValue();
      if (cur && !options.some(o => String(o.id) === String(cur))) {
        instanceRef.current.setValue('', true);
      }
      return;
    }

    if (options.length === 0) return;

    instanceRef.current = new TomSelect(selectRef.current, {
      valueField: 'id',
      labelField: 'nama',
      searchField: ['nama', 'jabatan'],
      placeholder,
      options,
      render: {
        option: (d, esc) => `<div style="padding:8px 12px;border-bottom:1px solid #f1f5f9">
          <div style="font-weight:600;color:#1e293b;font-size:13px">${esc(d.nama)}</div>
          ${d.jabatan ? `<div style="font-size:11px;color:#64748b">${esc(d.jabatan)}</div>` : ''}
        </div>`,
        item: (d, esc) => `<div style="font-weight:500;font-size:13px">${esc(d.nama)}</div>`,
      },
      onChange: (v) => onChange(v),
    });

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [options]);

  return null;
};

// ─── Location & Employee Matching Helpers ─────────────────────────────────────
export const isKantorAgen = (kantor) => {
  if (!kantor) return false;
  return String(kantor).trim().toLowerCase().startsWith('agen');
};

export const extractLocationKeywords = (kantorName) => {
  if (!kantorName) return [];
  const lower = String(kantorName).toLowerCase().trim();

  // Jika Cilacap atau ada kata ruang
  if (lower.includes('cilacap') || lower.includes('ruang')) {
    return ['cilacap'];
  }

  // Jika Tritih dalam kurung "KP GATOT SUBROTO ( TRITIH )"
  const bracketMatch = lower.match(/\(([^)]+)\)/);
  const inBrackets = bracketMatch ? bracketMatch[1].trim() : '';

  // Hapus kata awalan umum seperti kp, counter, agen, cabang, kantor, jne
  const cleaned = lower
    .replace(/\(.*?\)/g, '')
    .split(/\s+/)
    .map(w => w.replace(/[^a-z0-9]/g, ''))
    .filter(w => w.length > 2 && !['kp', 'counter', 'agen', 'kantor', 'cabang', 'jne', 'pos', 'sales'].includes(w));

  const keywords = [...cleaned];
  if (inBrackets && inBrackets.length > 2) {
    keywords.push(inBrackets.replace(/[^a-z0-9]/g, ''));
  }
  return keywords;
};

export const isEmployeeInKantor = (karyawan, targetKantor) => {
  if (!targetKantor) return true;
  const kKantor = String(karyawan?.kantor || karyawan?.nama_kantor || karyawan?.cabang || '').toLowerCase().trim();
  if (!kKantor) return false;

  const targetLower = String(targetKantor).toLowerCase().trim();

  // 1. Jika target adalah Cilacap (atau Kantor Cilacap)
  if (targetLower.includes('cilacap') || targetLower.includes('ruang')) {
    return kKantor.includes('cilacap') || kKantor === 'pusat' || kKantor.includes('tritih');
  }

  // 2. Exact match atau includes langsung
  if (kKantor === targetLower || targetLower.includes(kKantor) || kKantor.includes(targetLower)) {
    return true;
  }

  // 3. Keyword matching (misal "KP SIDAREJA" vs "Sidareja")
  const keywords = extractLocationKeywords(targetKantor);
  if (keywords.length === 0) return true;

  return keywords.some(kw => kKantor.includes(kw) || kw.includes(kKantor));
};

export const buildOfficeList = (karyawanList = [], extraLocations = []) => {
  const set = new Set();
  let hasCilacap = false;

  const processName = (rawName) => {
    if (!rawName) return;
    const name = String(rawName).trim();
    if (!name) return;
    const low = name.toLowerCase();

    // 1. Awalan Agen
    if (low.startsWith('agen')) {
      set.add(name);
      return;
    }

    // 2. Awalan KP
    if (low.startsWith('kp')) {
      set.add(name);
      return;
    }

    // 3. Awalan Counter
    if (low.startsWith('counter')) {
      set.add(name);
      return;
    }

    // 4. Ada kata Cilacap atau Ruang -> dijadikan satu "Kantor Cilacap"
    if (low.includes('cilacap') || low.includes('ruang')) {
      hasCilacap = true;
      return;
    }
  };

  // Proses dari asset-locations
  extraLocations.forEach(processName);

  // Proses dari karyawanList
  karyawanList.forEach(k => {
    if (k?.kantor) {
      const kLow = k.kantor.toLowerCase().trim();
      if (kLow.includes('cilacap') || kLow.includes('ruang')) {
        hasCilacap = true;
      } else if (kLow.startsWith('agen') || kLow.startsWith('kp') || kLow.startsWith('counter')) {
        processName(k.kantor);
      }
    }
  });

  const result = [];
  if (hasCilacap) {
    result.push('Kantor Cilacap');
  }

  const sortedOthers = [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  result.push(...sortedOthers);

  return result.map(name => ({ id: name, nama: name }));
};

// ─── ItemCard ────────────────────────────────────────────────────────────────
const ItemCard = React.memo(({ item, index, karyawanList, officeList, onUpdate, onRemove, canRemove }) => {
  const kantorRef = useRef(null);
  const penerimRef = useRef(null);

  const isAgen = useMemo(() => isKantorAgen(item.kantor), [item.kantor]);

  const uniqueKantorList = useMemo(() => {
    if (officeList && officeList.length > 0) return officeList;
    return buildOfficeList(karyawanList);
  }, [karyawanList, officeList]);

  const filteredKaryawan = useMemo(() => {
    if (!item.kantor) return karyawanList;
    return karyawanList.filter(k => isEmployeeInKantor(k, item.kantor));
  }, [karyawanList, item.kantor]);

  const handleKantorChange = useCallback((v) => {
    const isTargetAgen = isKantorAgen(v);
    onUpdate(item._key, {
      kantor: v,
      recipient_id: isTargetAgen ? v : '',
      recipient_name: isTargetAgen ? v : '',
      recipient_type: isTargetAgen ? 'agen' : 'internal',
    });
  }, [item._key, onUpdate]);

  const handlePenerimaChange = useCallback((v) => {
    const sel = karyawanList.find(k => String(k.id) === v);
    onUpdate(item._key, {
      recipient_id: v,
      recipient_name: sel ? sel.nama : '',
      recipient_type: 'internal',
    });
  }, [item._key, karyawanList, onUpdate]);

  const handlePhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => onUpdate(item._key, { photo: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
      style={{
        backgroundColor: '#f8fafc',
        borderRadius: 20,
        padding: '18px 16px',
        border: '1.5px solid #e2e8f0',
        position: 'relative',
      }}
    >
      {/* Header item */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', backgroundColor: '#dbeafe', padding: '4px 12px', borderRadius: 20 }}>
          📦 Item #{index + 1}
        </div>
        {canRemove && (
          <button type="button" onClick={() => onRemove(item._key)}
            style={{ background: '#fee2e2', border: 'none', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
            <Trash2 size={13} /> Hapus
          </button>
        )}
      </div>

      {/* Category toggle */}
      <div style={{ display: 'flex', backgroundColor: '#e9f0fb', padding: 3, borderRadius: 10, gap: 3, marginBottom: 12 }}>
        {['Barang', 'Dokumen'].map(cat => (
          <button key={cat} type="button"
            onClick={() => onUpdate(item._key, { category: cat })}
            style={{
              flex: 1, padding: '8px', border: 'none', borderRadius: 8, fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
              backgroundColor: item.category === cat ? '#fff' : 'transparent',
              color: item.category === cat ? '#2563eb' : '#64748b',
              boxShadow: item.category === cat ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s',
            }}>
            {cat === 'Barang' ? '📦 Barang' : '📄 Dokumen'}
          </button>
        ))}
      </div>

      {/* Nama barang */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Package size={13} /> Nama {item.category}
        </label>
        <input
          type="text"
          placeholder={`Cth: ${item.category === 'Barang' ? 'Paket Shopee' : 'Surat Jalan'}`}
          value={item.item_name}
          onChange={e => onUpdate(item._key, { item_name: e.target.value })}
          style={{ padding: '11px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box', width: '100%' }}
        />
      </div>

      {/* Kantor Tujuan */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MapPin size={13} /> Kantor Tujuan
        </label>
        <select ref={kantorRef} autoComplete="off" style={{ width: '100%' }}>
          <option value="">Pilih Kantor...</option>
        </select>
        <TomSelectField
          selectRef={kantorRef}
          options={uniqueKantorList}
          onChange={handleKantorChange}
          placeholder="Pilih Kantor..."
        />
      </div>

      {/* Ditujukan Untuk: Sembunyikan jika tujuan adalah Agen */}
      {isAgen ? (
        <div style={{
          backgroundColor: '#eff6ff',
          border: '1.5px solid #bfdbfe',
          borderRadius: 14,
          padding: '11px 14px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>🏢</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>
              Tujuan Agen: {item.kantor}
            </div>
            <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
              Barang / dokumen langsung ditujukan untuk Agen, tidak memerlukan penerima personal.
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
            <User size={13} /> Ditujukan Untuk
          </label>
          <select ref={penerimRef} autoComplete="off" style={{ width: '100%' }}>
            <option value="">Cari penerima...</option>
          </select>
          <TomSelectField
            selectRef={penerimRef}
            options={filteredKaryawan}
            onChange={handlePenerimaChange}
            placeholder="Cari penerima..."
          />
          {item.kantor && filteredKaryawan.length === 0 && (
            <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>⚠️ Tidak ada karyawan di kantor ini</div>
          )}
        </div>
      )}

      {/* Keterangan */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={13} /> Keterangan (Opsional)
        </label>
        <textarea
          placeholder="Catatan tambahan..."
          rows={2}
          value={item.notes}
          onChange={e => onUpdate(item._key, { notes: e.target.value })}
          style={{ padding: '10px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', resize: 'none', backgroundColor: '#fff', width: '100%', boxSizing: 'border-box' }}
        />
      </div>

      {/* Foto */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Camera size={13} /> Foto Bukti (Opsional)
        </label>
        <input type="file" accept="image/*" id={`photo-${item._key}`} style={{ display: 'none' }} onChange={e => handlePhoto(e.target.files[0])} />
        <label htmlFor={`photo-${item._key}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: item.photo ? 'auto' : 64, borderRadius: 12, border: item.photo ? 'none' : '2px dashed #cbd5e1', backgroundColor: item.photo ? 'transparent' : '#f1f5f9', cursor: 'pointer', overflow: 'hidden', fontSize: 12, color: '#64748b', fontWeight: 500 }}>
          {item.photo
            ? <img src={item.photo} alt="preview" style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 12 }} />
            : <><Camera size={16} color="#94a3b8" /> Ambil Foto</>}
        </label>
      </div>
    </motion.div>
  );
});

// ─── Main Form ────────────────────────────────────────────────────────────────
const TitipForm = ({ onClose }) => {
  const [items, setItems] = useState([newItem()]);
  const [step, setStep] = useState(STEP.FORM);
  const [savedItems, setSavedItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [karyawanList, setKaryawanList] = useState([]);
  const [extraLocations, setExtraLocations] = useState([]);
  const [showDeliveryModes, setShowDeliveryModes] = useState(false);

  useEffect(() => {
    let stored = [];
    try {
      stored = JSON.parse(localStorage.getItem('karyawan_pengganti_list') || '[]');
    } catch {
      stored = [];
    }
    if (Array.isArray(stored) && stored.length > 0) {
      setKaryawanList(stored);
    }

    // Jika karyawan_pengganti_list belum ada di localStorage (misal pada akun Agen), ambil dari server
    const fetchKaryawan = async () => {
      try {
        const res = await fetch('https://jne-cilacap.com/slip/public/api/riwayat-absen/108');
        const json = await res.json();
        if (json.success && Array.isArray(json.karyawan_pengganti_list) && json.karyawan_pengganti_list.length > 0) {
          setKaryawanList(json.karyawan_pengganti_list);
          try {
            localStorage.setItem('karyawan_pengganti_list', JSON.stringify(json.karyawan_pengganti_list));
          } catch (e) {
            console.warn('Gagal simpan karyawan_pengganti_list ke localStorage:', e);
          }
        }
      } catch (err) {
        console.warn('Gagal ambil data karyawan pengganti di TitipForm:', err);
      }
    };

    if (!stored || stored.length === 0) {
      fetchKaryawan();
    }

    // Ambil daftar lokasi lengkap dari server (termasuk agen)
    const fetchLocations = async () => {
      try {
        const res = await fetch('https://jne-cilacap.com/slip/public/api/asset-locations');
        const json = await res.json();
        if (json.status === 'success' && Array.isArray(json.data)) {
          const locNames = json.data.map(l => l.name?.trim()).filter(Boolean);
          setExtraLocations(locNames);
        }
      } catch {
        // silent fallback
      }
    };
    fetchLocations();
  }, []);

  const officeList = useMemo(() => {
    return buildOfficeList(karyawanList, extraLocations);
  }, [karyawanList, extraLocations]);

  const handleUpdateItem = useCallback((key, patch) => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, ...patch } : it));
  }, []);

  const handleRemoveItem = useCallback((key) => {
    setItems(prev => prev.filter(it => it._key !== key));
  }, []);

  const handleAddItem = () => setItems(prev => [...prev, newItem()]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const invalid = items.find(it => {
      const isAgen = isKantorAgen(it.kantor);
      if (isAgen) {
        return !it.item_name.trim() || !it.kantor.trim();
      }
      return !it.item_name.trim() || !it.kantor.trim() || !it.recipient_id;
    });

    if (invalid) {
      Swal.fire('Peringatan', 'Setiap item harus diisi: Nama, Kantor Tujuan, dan Penerima (kecuali tujuan Agen)', 'warning');
      return;
    }

    setIsSubmitting(true);
    const sender = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const senderName = `${sender.nama_depan || ''} ${sender.nama_belakang || ''}`.trim();

    // Map payload items
    const payloadItems = items.map(it => {
      const isAgen = isKantorAgen(it.kantor);
      const recipientId = isAgen ? (it.recipient_id || it.kantor) : String(it.recipient_id);
      const recipientName = isAgen ? (it.recipient_name || it.kantor) : it.recipient_name;
      const itemId = 'TITIP-' + Math.random().toString(36).substr(2, 9).toUpperCase();
      return {
        id: itemId,
        item_name: it.item_name,
        category: it.category,
        notes: it.notes || '',
        photo: it.photo || null,
        kantor: it.kantor,
        recipient_id: recipientId,
        recipient_name: recipientName,
        recipient_type: isAgen ? 'agen' : (it.recipient_type || 'internal'),
        tracking_token: null,
        sender_id: String(sender.id),
        sender_name: senderName,
      };
    });

    let success = false;
    // 1. Coba simpan ke API Server
    try {
      const res = await fetch('https://jne-cilacap.com/slip/public/api/handovers/create-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payloadItems })
      });
      const json = await res.json();
      if (json.status === 'success') {
        success = true;
        const payloadById = new Map(payloadItems.map(item => [String(item.id), item]));
        const savedFromServer = json.data.map(item => ({
          ...payloadById.get(String(item.id)),
          ...item,
          recipient_type: payloadById.get(String(item.id))?.recipient_type,
          tracking_token: payloadById.get(String(item.id))?.tracking_token,
        }));
        const existing = readHandovers();
        writeHandovers([...savedFromServer, ...existing]);
        localStorage.setItem('data_titipan_last_fetch', '0'); // Invalidate cache agar data terbaru diambil dari server
        window.dispatchEvent(new Event('storage'));

        setSavedItems(savedFromServer);
        setSelectedIds(savedFromServer.map(i => i.id));
      }
    } catch (e) {
      console.warn('API store batch failed, falling back to local:', e);
    }

    // 2. Fallback Lokal (Offline)
    if (!success) {
      const newSaved = payloadItems.map(it => ({
        ...it,
        current_holder_id: sender.id,
        current_holder_name: senderName,
        timestamp: new Date().toISOString(),
        status: 'Diproses',
        timeline: [
          {
            status: 'Dibuat',
            actor_id: sender.id,
            actor_name: senderName,
            timestamp: new Date().toISOString(),
            notes: `Handover dibuat oleh ${senderName}`
          }
        ]
      }));

      const existing = readHandovers();
      writeHandovers([...newSaved, ...existing]);
      window.dispatchEvent(new Event('storage'));

      setSavedItems(newSaved);
      setSelectedIds(newSaved.map(i => i.id));
    }

    setIsSubmitting(false);
    setStep(STEP.SELECT);
  };

  // ── Select helpers ────────────────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelectedIds(prev => prev.length === savedItems.length ? [] : savedItems.map(i => i.id));

  const openDeliveryModes = () => {
    const selected = savedItems.filter(item => selectedIds.includes(item.id));
    const destinations = new Set(selected.map(item => `${item.recipient_id}|${item.kantor}`));
    if (destinations.size > 1) {
      Swal.fire('Tujuan Berbeda', 'Pilih item dengan penerima akhir dan kantor tujuan yang sama.', 'warning');
      return;
    }
    setShowDeliveryModes(true);
  };

  // ── QR payload ────────────────────────────────────────────────────────────
  const chosenItems = savedItems.filter(i => selectedIds.includes(i.id));
  const externalItems = savedItems.filter(item => item.recipient_type === 'external');
  const trackingUrl = useMemo(() => {
    if (!externalItems.length || typeof window === 'undefined') return '';
    const value = window.btoa(JSON.stringify(externalItems.map(item => item.id)));
    return `${window.location.origin}/handover/track?ref=${encodeURIComponent(value)}`;
  }, [savedItems]);

  const copyTrackingUrl = async () => {
    await navigator.clipboard.writeText(trackingUrl);
    Swal.fire({ icon: 'success', title: 'Link Disalin', timer: 1300, showConfirmButton: false });
  };

  const shareTrackingUrl = async () => {
    if (navigator.share) {
      await navigator.share({ title: 'Pantau Handover', text: 'Pantau progres serah terima barang melalui link berikut:', url: trackingUrl });
    } else {
      await copyTrackingUrl();
    }
  };

  const qrPayload = useMemo(() => {
    if (!chosenItems.length) return '';
    return JSON.stringify({
      ids: chosenItems.map(i => i.id),
      items: chosenItems.map(i => ({ id: i.id, item: i.item_name, to: i.recipient_name, kantor: i.kantor })),
    });
  }, [selectedIds, savedItems]);

  const handleDeliveryMode = async ({ mode, receiver_id, receiver_name, notes, photo }) => {
    if (mode === HANDOVER_MODES.QR) {
      setStep(STEP.QR);
      return;
    }

    const sender = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const actorName = `${sender.nama_depan || ''} ${sender.nama_belakang || ''}`.trim();
    const timestamp = new Date().toISOString();
    const normalizeName = value => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const chosenIds = new Set(selectedIds.map(String));
    const updatedChosen = savedItems.filter(item => chosenIds.has(String(item.id))).map(item => {
      const isFinalReceiver = mode === HANDOVER_MODES.MANUAL
        && normalizeName(receiver_name) === normalizeName(item.recipient_name);
      const status = isFinalReceiver
        ? 'Selesai'
        : mode === HANDOVER_MODES.UNATTENDED ? 'Menunggu Diterima' : 'Diserahkan';
      const eventNotes = mode === HANDOVER_MODES.UNATTENDED
        ? `Ditinggalkan untuk ${receiver_name}. ${notes}`
        : isFinalReceiver
          ? `Diterima langsung oleh penerima akhir (${receiver_name})${notes ? `. ${notes}` : ''}`
          : `Diserahkan langsung kepada perantara (${receiver_name})${notes ? `. ${notes}` : ''}`;

      return {
        ...item,
        status,
        handover_mode: mode,
        actual_receiver_id: receiver_id || null,
        actual_receiver_name: receiver_name,
        delivery_notes: notes,
        delivery_photo: photo || null,
        delivered_at: timestamp,
        pending_receiver_id: mode === HANDOVER_MODES.UNATTENDED ? String(receiver_id) : null,
        pending_receiver_name: mode === HANDOVER_MODES.UNATTENDED ? receiver_name : null,
        confirmation_status: isFinalReceiver ? 'confirmed' : 'pending',
        ...(isFinalReceiver ? {
          current_holder_id: item.recipient_id,
          current_holder_name: item.recipient_name,
          receiver_id: item.recipient_id,
          receiver_name,
          receive_time: timestamp,
        } : mode === HANDOVER_MODES.MANUAL ? {
          current_holder_id: receiver_id || '',
          current_holder_name: receiver_name,
        } : {}),
        timeline: [...(item.timeline || []), {
          status,
          actor_id: String(sender.id || ''),
          actor_name: actorName,
          receiver_id: receiver_id || null,
          receiver_name,
          receiver_type: isFinalReceiver ? 'final' : 'transit',
          method: mode,
          timestamp,
          notes: eventNotes,
          photo: photo || null,
        }],
      };
    });

    try {
      const response = await fetch('https://jne-cilacap.com/slip/public/api/handovers/deliver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: updatedChosen.map(item => item.id),
          mode,
          receiver_id: receiver_id || null,
          receiver_name,
          notes,
          photo,
          actor_id: String(sender.id || ''),
          actor_name: actorName,
          timestamp,
        }),
      });
      if (!response.ok) throw new Error('Endpoint penyerahan belum tersedia');
    } catch (error) {
      console.warn('Penyerahan disimpan lokal dan menunggu sinkronisasi:', error);
    }

    const updates = new Map(updatedChosen.map(item => [String(item.id), item]));
    const allItems = readHandovers().map(item => updates.get(String(item.id)) || item);
    writeHandovers(allItems);
    setSavedItems(previous => previous.map(item => updates.get(String(item.id)) || item));
    localStorage.setItem('data_titipan_last_fetch', '0');
    window.dispatchEvent(new Event('handoverDataUpdated'));
    window.dispatchEvent(new Event('badgeUpdate'));
    Swal.fire('Tercatat', mode === HANDOVER_MODES.UNATTENDED
      ? 'Bukti penempatan masuk timeline dan menunggu konfirmasi penerima.'
      : 'Serah terima telah dicatat.', 'success');
    onClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: FORM
  // ─────────────────────────────────────────────────────────────────────────
  if (step === STEP.FORM) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        style={{ backgroundColor: '#fff', borderRadius: 24, padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', maxWidth: 540, margin: '0 auto' }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 18, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 10px' }}>📦</div>
          <h2 style={{ fontSize: 19, fontWeight: 700, color: '#1e293b', margin: 0 }}>Masukan Data Barang/Dokumen</h2>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Setiap item bisa memiliki kantor tujuan & penerima yang berbeda
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Item list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Daftar Item</span>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{items.length} item</span>
            </div>

            <AnimatePresence>
              {items.map((it, idx) => (
                <ItemCard
                  key={it._key}
                  item={it}
                  index={idx}
                  karyawanList={karyawanList}
                  officeList={officeList}
                  onUpdate={handleUpdateItem}
                  onRemove={handleRemoveItem}
                  canRemove={items.length > 1}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Add Item */}
          <button type="button" onClick={handleAddItem}
            style={{ width: '100%', padding: '12px', borderRadius: 14, border: '2px dashed #2563eb', backgroundColor: '#eff6ff', color: '#2563eb', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
            <Plus size={18} /> Tambah Item Lain
          </button>

          {/* Submit */}
          <button type="submit" disabled={isSubmitting}
            style={{ width: '100%', padding: '15px', borderRadius: 16, border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 4px 12px rgba(37,99,235,0.2)', opacity: isSubmitting ? 0.7 : 1 }}>
            <Send size={17} />
            {isSubmitting ? 'Menyimpan...' : `Simpan ${items.length} Item → Pilih untuk Diserahkan`}
          </button>
        </form>
      </motion.div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: SELECT
  // ─────────────────────────────────────────────────────────────────────────
  if (step === STEP.SELECT) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
        style={{ backgroundColor: '#fff', borderRadius: 24, padding: '28px 20px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', maxWidth: 540, margin: '0 auto' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: '0 0 6px' }}>{savedItems.length} Item Tersimpan</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
            Centang item yang ingin <b>diserahkan sekarang</b>
          </p>
        </div>

        {trackingUrl && (
          <div style={{ padding: 14, borderRadius: 14, background: '#ecfdf5', border: '1px solid #a7f3d0', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#065f46', marginBottom: 5 }}>Link Tracking Penerima</div>
            <div style={{ fontSize: 10, color: '#047857', wordBreak: 'break-all', background: '#fff', borderRadius: 8, padding: 8 }}>{trackingUrl}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
              <button type="button" onClick={copyTrackingUrl} style={{ flex: 1, padding: 9, border: 'none', borderRadius: 9, background: '#059669', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Salin Link</button>
              <button type="button" onClick={shareTrackingUrl} style={{ flex: 1, padding: 9, border: '1px solid #059669', borderRadius: 9, background: '#fff', color: '#047857', fontWeight: 700, cursor: 'pointer' }}>Bagikan</button>
            </div>
          </div>
        )}

        {/* Select All */}
        <div onClick={toggleAll}
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 14px', backgroundColor: '#f8fafc', borderRadius: 12, marginBottom: 12, border: '1px solid #e2e8f0', userSelect: 'none' }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${selectedIds.length === savedItems.length ? '#2563eb' : '#cbd5e1'}`, backgroundColor: selectedIds.length === savedItems.length ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 }}>
            {selectedIds.length === savedItems.length && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
            Pilih Semua <span style={{ color: '#94a3b8', fontWeight: 500 }}>({selectedIds.length}/{savedItems.length})</span>
          </span>
        </div>

        {/* Item list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
          {savedItems.map((it, idx) => {
            const isChecked = selectedIds.includes(it.id);
            return (
              <motion.div key={it.id} onClick={() => toggleSelect(it.id)} whileTap={{ scale: 0.98 }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 16, border: `2px solid ${isChecked ? '#2563eb' : '#e2e8f0'}`, backgroundColor: isChecked ? '#eff6ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s', userSelect: 'none' }}>
                {/* Checkbox */}
                <div style={{ width: 23, height: 23, borderRadius: 7, border: `2px solid ${isChecked ? '#2563eb' : '#cbd5e1'}`, backgroundColor: isChecked ? '#2563eb' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                  {isChecked && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                </div>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{it.category === 'Barang' ? '📦' : '📄'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.item_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    → <b>{it.recipient_name}</b> · {it.kantor}
                  </div>
                </div>
                <div style={{ fontSize: 9, fontFamily: 'monospace', color: '#2563eb', backgroundColor: '#dbeafe', padding: '3px 7px', borderRadius: 7, flexShrink: 0 }}>
                  {it.id.split('-')[1]}
                </div>
              </motion.div>
            );
          })}
        </div>

        <button
          disabled={selectedIds.length === 0}
          onClick={openDeliveryModes}
          style={{ width: '100%', padding: '15px', borderRadius: 16, border: 'none', backgroundColor: selectedIds.length === 0 ? '#e2e8f0' : '#2563eb', color: selectedIds.length === 0 ? '#94a3b8' : '#fff', fontSize: 14, fontWeight: 700, cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: selectedIds.length === 0 ? 'none' : '0 4px 14px rgba(37,99,235,0.25)', transition: 'all 0.2s', marginBottom: 10 }}>
          <QrCode size={18} />
          Pilih Cara Serah Terima ({selectedIds.length} item)
          <ChevronRight size={16} />
        </button>

        <button onClick={onClose}
          style={{ width: '100%', padding: '12px', borderRadius: 14, border: '1.5px solid #e2e8f0', backgroundColor: 'transparent', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Tutup – Serahkan Nanti
        </button>
        <HandoverDeliveryModal
          open={showDeliveryModes}
          itemCount={selectedIds.length}
          defaultRecipientName={chosenItems[0]?.recipient_name}
          onClose={() => setShowDeliveryModes(false)}
          onSubmit={handleDeliveryMode}
        />
      </motion.div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP: QR
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      style={{ backgroundColor: '#fff', borderRadius: 24, padding: '30px 24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', maxWidth: 540, margin: '0 auto', textAlign: 'center' }}
    >
      <div style={{ width: 68, height: 68, borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', color: '#22c55e' }}>
        <ShieldCheck size={36} />
      </div>
      <h2 style={{ fontSize: 19, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>QR Serah Terima</h2>
      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 24 }}>
        Tunjukkan ke Security / Penerima · <b>{chosenItems.length} item</b>
      </p>

      {/* QR */}
      <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: 20, border: '1px solid #e2e8f0', display: 'inline-flex', marginBottom: 22 }}>
        <div style={{ backgroundColor: '#fff', padding: 14, borderRadius: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <QRCode value={qrPayload} size={180} level="H" />
        </div>
      </div>

      {/* Item summary */}
      <div style={{ textAlign: 'left', backgroundColor: '#f1f5f9', padding: '16px', borderRadius: 16, marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 10 }}>Daftar Item</div>
        {chosenItems.map((it, i) => (
          <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: i < chosenItems.length - 1 ? 10 : 0, paddingBottom: i < chosenItems.length - 1 ? 10 : 0, borderBottom: i < chosenItems.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ marginTop: 1 }}>{it.category === 'Barang' ? '📦' : '📄'}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{it.item_name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>→ {it.recipient_name} · {it.kantor}</div>
              </div>
            </div>
            <span style={{ fontSize: 9, fontFamily: 'monospace', color: '#64748b', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: 5, flexShrink: 0, marginTop: 2 }}>
              {it.id.split('-')[1]}
            </span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button onClick={() => setStep(STEP.SELECT)}
          style={{ padding: '12px', borderRadius: 13, border: '1.5px solid #2563eb', backgroundColor: 'transparent', color: '#2563eb', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <CheckSquare size={15} /> Ubah Pilihan Item
        </button>
        <button onClick={onClose}
          style={{ padding: '14px', borderRadius: 15, border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,99,235,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <ShieldCheck size={17} /> Selesai
        </button>
      </div>
    </motion.div>
  );
};

export default TitipForm;
